/*
 * Copyright (c) 2025 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {useAppDispatch, useAppSelector} from "../../../../../../../../redux/hooks"
import {
  closeViewDataSetErrorsDialog,
  EViewDataSetErrorsDialogDataType,
  GreenLineError,
  selectGreenLineError,
  selectGreenLineName,
  selectViewDataSetErrorsDialog
} from "../../../../../../../../redux/ducks/SensingSystemArchitectureDesign"
import Flexbox from "../../../../../../../components/Containers/ArgosFlexbox"
import Button from "../../../../../../../components/Buttons/ArgosButton"
import {useState} from "react"
import {Typography} from "@mui/material"
import {ModalDialog} from "../../../../../../../components/ModalDialog/ModalDialog"
import ExpandCircleDownSharpIcon from '@mui/icons-material/ExpandCircleDownSharp'
import {renderGreenLineActionType} from "../../../../../common/helpers"
import {dateToLocaleString} from "../../../../../../../../helpers/helpers"


/**
 * Dialog to view the error message details for a data set.
 */
export const ViewDataSetErrorDialog = () => {
  const dispatch = useAppDispatch()

  const isOpen = useAppSelector(state => selectViewDataSetErrorsDialog(state).isOpen)
  const dataSetId = useAppSelector(state => selectViewDataSetErrorsDialog(state).dataSetId)
  const dataSetName = useAppSelector(state => selectGreenLineName(state, dataSetId ?? ''))

  const dataType = useAppSelector(state => selectViewDataSetErrorsDialog(state).dataType)
  const sensorIdToBlueLineRunState = useAppSelector(state => state.sensingSystemArchitectureDesign.sensorIdToBlueLineRunState)
  const sensorIdToViolationsRunState = useAppSelector(state => state.sensingSystemArchitectureDesign.sensorIdToViolationsRunState)
  const blueLineError = sensorIdToBlueLineRunState[dataSetId ?? '']?.error ?? null
  const violationsError = sensorIdToViolationsRunState[dataSetId ?? '']?.error ?? null


  const greenLineError = useAppSelector(state => selectGreenLineError(state, dataSetId ?? ''))
  const dataSetError: GreenLineError | null = dataType === EViewDataSetErrorsDialogDataType.BlueLine ? blueLineError : dataType === EViewDataSetErrorsDialogDataType.Violations ? violationsError : greenLineError

  const closeDialog = () => {
    setShowStackTrace(false)
    dispatch(closeViewDataSetErrorsDialog())
  }

  const [showStackTrace, setShowStackTrace] = useState<boolean>(false)

  const toggleStackTrace = () => {
    setShowStackTrace(!showStackTrace)
  }

  /** Rotate icon for expand/collapse on toggle */
  const rotatedIconStyle = {
    transform: showStackTrace ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'transform 0.3s'
  }

  return (
    <ModalDialog
      open={isOpen}
      handleClose={closeDialog}
      title={dataSetName ? `Error in ${dataSetName}` : 'Error'}
      maxWidth={'md'}
    >
      <Flexbox vertical style={{padding: '0px', gap: '20px'}}>
        {!dataSetError && <Flexbox vertical style={{gap: '20px'}}>
          <Typography>Unable to display the error message. Please try again or contact the Argos team for assistance.</Typography>
        </Flexbox>}
        {dataSetError && <Flexbox vertical style={{ gap: '20px'}}>
          <Typography>
            {dataSetError.exception}
          </Typography>
          <Typography>
            Occurred on {dateToLocaleString(new Date(dataSetError.timestamp))} during {renderGreenLineActionType(dataSetError.activity, dataType)}
          </Typography>
          {showStackTrace && (
            <pre
              style={{
                overflowX: 'auto', backgroundColor: '#f5f5f5', padding: '10px',
                borderRadius: '5px', whiteSpace: 'pre-wrap',
                maxHeight: 600
              }}
            >
              {dataSetError.stackTrace}
            </pre>
          )}
          <Button
            onClick={toggleStackTrace}
            variant='text'
            sx={{textTransform: 'none'}}
            startIcon={<ExpandCircleDownSharpIcon sx={rotatedIconStyle} />}
            fontSize={16}
          >
            {showStackTrace ? 'Hide logs' : 'Show logs'}
          </Button>
        </Flexbox>}
      </Flexbox>
    </ModalDialog>
  )
}
